import { chromeTitles } from '@featit/shared/i18n/messages/en/chromeTitles';
import { shell } from '@featit/shared/i18n/messages/en/shell';
import type { MessageKey } from '@featit/shared/i18n/messages/index';
import { registerEnglishArea } from '@/i18n/catalogue';
import type { TFn } from '@/i18n/LocaleProvider';
import { BACK_LABEL_MAX } from './routeParents';

registerEnglishArea('shell', shell);

/**
 * I-1925, T-0842: the `chromeTitles` entries that are names the shell computes for a page (a tab's,
 * the sidebar's, the back row's). Only these are drawn by their English: a record's own name that
 * reads like another chrome title (a page called "Run") is drawn as it is.
 * `shell-page-names.guard.test.ts` holds every tab's title here, with its German.
 */
export const SHELL_PAGE_NAME_KEYS = [
  'activity',
  'agents',
  'chooseWorkArea',
  'commit',
  'conversations',
  'decisions',
  'decisionsForMe',
  'decisionsWaiting',
  'document',
  'documents',
  'editWorkAreas',
  'feedback',
  'file',
  'home',
  'invitation',
  'items',
  'more',
  'myIdeas',
  'newDecision',
  'newSchedule',
  'newWorkItem',
  'organisation',
  'parts',
  'profile',
  'roadmap',
  'schedule',
  'schedules',
  'settings',
  'sharedWithYou',
  'tasks',
  'tasksForMe',
  'tasksWaiting',
  'views',
  'workAreas',
  'workItem',
  'workItems',
] as const satisfies readonly (keyof typeof chromeTitles)[];

/** The English page names the shell computes, to their `chromeTitles` keys. */
const PAGE_NAME_KEYS = new Map<string, string>(
  SHELL_PAGE_NAME_KEYS.map((key) => [chromeTitles[key], key]),
);

/**
 * I-1925: a page's name as the shell computed it — a tab's, a pushed list's, the bar's — in the
 * reader's language. The shell keeps computing it in English, its identity for the page (a label
 * is compared, stored with a history entry, matched to a tab); only the drawing translates it. A
 * record's key or a page's own name (a view, a custom page) is not one of them and is drawn as it is.
 */
export function pageName(t: TFn, english: string): string {
  const key = PAGE_NAME_KEYS.get(english);
  return key ? t(`chromeTitles.${key}` as MessageKey) : english;
}

/**
 * The back control's words for a label the shell computed (`useHistoryBackLabel`): what it shows,
 * and its accessible name. A name longer than the row has room for in the reader's language shows
 * as Back (S-4: at most 12 characters), and its accessible name still says where it goes.
 */
export function backWords(t: TFn, label: string): { text: string; name: string } {
  const back = t('shell.back');
  // T-0838: a label already said in the reader's language may be their Back too.
  if (label === 'Back' || label === back) return { text: back, name: back };
  const shown = pageName(t, label);
  const name = t('shell.backTo', { label: shown });
  return { text: shown.length <= BACK_LABEL_MAX ? shown : back, name };
}
